import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { error as toastError, success as toastSuccess } from '@app/snackbars';
import { Tooltip } from '@app/ui/tooltip';

import { RULES_IMPORT_CATALOGUE } from '../gql';
import { RuleFamily } from '../types';
import { getGraphQLErrorMessage, isAlreadyImportedMessage } from '../utils';

export interface RulesEmptyStateProps {
  readonly family: RuleFamily;
  readonly editable: boolean;
  readonly onImported: () => void;
}

// `editable` gates whether the Import affordance shows at all — it is the server's own answer
// (`rulesFamilySummaryGetMany`) about whether the import mutation would be accepted, so re-deriving
// it from the family here would be a second copy of that list, free to disagree with the API. Both
// families are editable today, so the `!editable` branch below is a fail-CLOSED fallback for a
// hypothetical future family, not a colour-specific message.
export const RulesEmptyState = ({ family, editable, onImported }: RulesEmptyStateProps) => {
  const { t } = useTranslation('common');
  const [importCatalogue, { loading }] = useMutation<{ rulesImportCatalogue: boolean }>(RULES_IMPORT_CATALOGUE);

  const handleImport = async () => {
    try {
      await importCatalogue({ variables: { family } });
      toastSuccess(t('rules.emptyState.importSuccess'));
      onImported();
    } catch (e) {
      const message = getGraphQLErrorMessage(e);

      // Still refetch on a genuine "already imported", so a stale empty state self-heals instead
      // of leaving this button clickable forever.
      if (isAlreadyImportedMessage(message)) {
        toastSuccess(message ?? t('rules.emptyState.importSuccess'));
        onImported();

        return;
      }

      toastError(message ?? t('general.error.somethingWentWrong'));
    }
  };

  return (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: editable ? 2 : 0 }}>
        {t(editable ? 'rules.emptyState.message' : 'rules.emptyState.notEditableMessage')}
      </Typography>

      {editable && (
        <Tooltip title={t('rules.emptyState.importTooltip') ?? undefined}>
          <span>
            <LoadingButton loading={loading} variant="outlined" color="secondary" onClick={handleImport}>
              {t('rules.emptyState.importButton')}
            </LoadingButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};
